import BookedRoom from "../models/BookedRoom";
import Room from "../models/Room";
import { initAllStores } from "./cache";
import { formatDate, formatDateTime } from "./date";
// import { useCallback } from "react";
// import { capitalizeFirst } from "./string";
// import { useToast } from "../context/ToastContext";


type BookingFormSetters = {
  setName: React.Dispatch<React.SetStateAction<string>>;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  setState: React.Dispatch<React.SetStateAction<string>>;
  setNationality: React.Dispatch<React.SetStateAction<string>>;
  setGender: React.Dispatch<React.SetStateAction<string>>;
  setIdType: React.Dispatch<React.SetStateAction<string>>;
  setIdNumber: React.Dispatch<React.SetStateAction<string>>;
  setOccupation: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  setDiscount: React.Dispatch<React.SetStateAction<string>>;
  setGuestNumber: React.Dispatch<React.SetStateAction<string>>;
  setVechicleNumber: React.Dispatch<React.SetStateAction<string>>;
};

export const autoFillBookingForm = async (
  bookingId: string,
  setters: BookingFormSetters
): Promise<string> => {
  try {
    // const { customer, booking, bookedRooms } = await getRoomBook(roomId);
    const cache = await initAllStores();
    const booking = await cache.getBy("bookings", { id: bookingId });
    const customer = booking.customer;

    // Populate booking form with data
    setters.setName(customer?.name ?? "");
    setters.setEmail(customer?.email ?? "");
    setters.setPhone(customer?.phone ?? "");
    setters.setAddress(customer?.address ?? "");
    setters.setState(customer?.state ?? "");
    setters.setNationality(customer.nationality ?? "");
    setters.setGender(customer?.gender ?? "");
    setters.setIdType(customer?.idType ?? "");
    setters.setIdNumber(customer?.idNumber ?? "");
    setters.setOccupation(customer?.occupation ?? "");
    setters.setVechicleNumber(customer?.vechicleNo ?? "");
    setters.setDescription(booking?.description ?? "");
    // setters.setGuestNumber(booking?.guestNumber ?? "");

    return customer.id;
  } catch (err: any) {
    console.error("Auto-fill booking form failed:", err);
    throw new Error("Auto-fill booking form failed");
  }
};

/**
 * Filters out booked rooms that are linked to reservation-type bookings.
 * 
 * @param {BookedRoom[]} bookedRooms - List of booked rooms to filter.
 * @param {any} cache - Cache utility with getBy method for fetching bookings.
 * @returns {Promise<BookedRoom[]>} - Booked rooms that are NOT reservations.
 */
export async function filterNonReservationRooms(
  bookedRooms: BookedRoom[],
  cache: any
): Promise<BookedRoom[]> {

  return bookedRooms.filter((b: BookedRoom) => b.bookingType !== "reservation")
}


/**
 * Check if a room is available for a new booking or extension
 * 
 * @param bookedRooms - Array of all booked rooms from IndexedDB
 * @param roomId - The target roomId
 * @param newCheckIn - The proposed new check-in date (ISO string)
 * @param newCheckOut - The proposed new check-out date (ISO string)
 * @param excludeBookingId - (Optional) bookingId to exclude from check (for extensions)
 * @returns {string | null} - Returns a conflict message if clash found, otherwise null
 */
export async function checkRoomAvailability(
  bookedRooms: BookedRoom[],
  roomId: string,
  newCheckIn: string,
  newCheckOut: string,
  excludeBookingId?: string
): Promise<string | null> {

  const cache = await initAllStores();

  const newIn = new Date(newCheckIn);
  const newOut = new Date(newCheckOut);

  const conflicting = bookedRooms.find((room) => {
    // skip inactive rooms
    if (!room.isUse) return false;
    if (excludeBookingId && room.id === excludeBookingId) return false;
    if (room.roomId !== roomId) return false;

    const existingIn = new Date(room.checkInDate);
    const existingOut = new Date(room.checkOutDate);

    // Overlap check
    const overlap =
      (newIn >= existingIn && newIn < existingOut) || // new check-in inside existing range
      (newOut > existingIn && newOut < existingOut) || // new check-out inside existing range
      (newIn <= existingIn && newOut >= existingOut); // completely covers existing range

    return overlap;
  });

  const room = await cache.getBy("rooms", { id: conflicting?.roomId });

  if (conflicting) {
    const typeLabel =
      conflicting.bookingType.toLocaleLowerCase() === "fulltime"
        ? "currently occupied"
        : conflicting.bookingType === "reservation"
          ? "reserved"
          : conflicting.bookingType; // fallback

    return (
      `Room ${room?.number || "N/A"} is ${typeLabel} ` +
      `from ${new Date(conflicting.checkInDate).toDateString()} ` +
      `to ${new Date(conflicting.checkOutDate).toDateString()} by ${conflicting.guestName}. ` +
      "Please choose another room or adjust the dates."
    );
  }

  return null; // no conflict
}

export const attachRoomDataToBookings = async (
  bookedRooms: BookedRoom[]
): Promise<(BookedRoom & {
  roomNumber: string;
  roomName: string,
  amountDisplay: string;
})[]> => {
  const cache = await initAllStores();

  // For each bookedRoom, get its related room info
  const enriched = await Promise.all(
    bookedRooms.map(async (bookedRoom) => {
      const room = await cache.getBy("rooms", { id: bookedRoom.roomId }) as Room | undefined;

      const bookingType = bookedRoom.bookingType;

      return {
        ...bookedRoom,
        time: formatDateTime(bookedRoom.checkInDate).split(",")[2],
        checkInDate: bookedRoom.checkInDate,
        checkOutDate: bookedRoom.checkOutDate,
        bookingType: bookingType.charAt(0).toUpperCase() + bookingType.slice(1),
        amountDisplay: bookedRoom.amount.toLocaleString(),
        roomNumber: room?.number || "N/A",
        paymentStatus: (bookedRoom.isPaid ? "Paid" : "Pending") as PaymentStatus,
        roomName: room?.name || "Unknown Room",
        roomRate: room?.cashPrice.toLocaleString(),
        headline: room?.headline,
        // room,
      };
    })
  );

  return enriched;
};


interface RoomSummary {
  roomName: string;
  quantity: number;
  totalAmount: number;
}

export function getBookingReceiptSummary(
  bookedRooms: BookedRoom[],
  selectedRooms: Room[]
): RoomSummary[] {
  const summaryMap = bookedRooms.reduce((acc, booking) => {
    const room = selectedRooms.find(r => r.id === booking.roomId);
    const roomName = room?.name ?? "Room";
    const amount = booking.amount;

    if (!acc[roomName]) {
      acc[roomName] = { roomName, quantity: 0, totalAmount: 0 };
    }

    acc[roomName].quantity += 1;
    acc[roomName].totalAmount += amount;

    return acc;
  }, {} as Record<string, RoomSummary>);

  // Convert the map to a list
  return Object.values(summaryMap);
}

